import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCompany, PageHeader, Money } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, FileDown, FileText, AlertTriangle } from "lucide-react";

export default function Reports() {
  const { companyId, cur } = useCompany();
  const [f, setF] = useState(null);
  const [anos, setAnos] = useState([]);

  useEffect(() => {
    if (!companyId) return;
    api.get("/reports/financials", { params: { company_id: companyId } }).then(({ data }) => setF(data));
    api.get("/anomalies", { params: { company_id: companyId } }).then(({ data }) => setAnos(data.anomalies));
  }, [companyId]);

  const dl = async (kind) => {
    const res = await api.get(`/reports/financials.${kind}`, { params: { company_id: companyId }, responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a");
    a.href = url; a.download = `etats_financiers.${kind === "pdf" ? "pdf" : "csv"}`; a.click(); URL.revokeObjectURL(url);
  };

  if (!f) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="reports-page">
      <PageHeader title="États financiers" subtitle="Compte de résultat, balance et détection d'anomalies." action={
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => dl("pdf")} data-testid="report-pdf-button"><FileText className="w-4 h-4 mr-1" />PDF</Button>
          <Button variant="outline" className="rounded-full" onClick={() => dl("csv")} data-testid="report-csv-button"><FileDown className="w-4 h-4 mr-1" />Excel/CSV</Button>
        </div>} />

      {anos.length > 0 && (
        <Card className="p-4 rounded-md border-amber-300 bg-amber-50 shadow-sm mb-5" data-testid="anomalies-card">
          <div className="flex items-center gap-2 font-display font-semibold text-amber-800 mb-2"><AlertTriangle className="w-5 h-5" /> {anos.length} anomalie(s) détectée(s)</div>
          <ul className="space-y-1 text-sm text-amber-900">{anos.map((a, i) => <li key={i} data-testid={`anomaly-${i}`}>• {a.label}</li>)}</ul>
        </Card>)}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 rounded-md border-border shadow-sm" data-testid="income-statement">
          <h2 className="font-display text-lg font-semibold mb-3">Compte de résultat</h2>
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">Produits</div>
          {f.produits.map((p) => <div key={p.code} className="flex justify-between text-sm py-0.5"><span>{p.code} {p.name}</span><Money value={p.amount} cur={cur} /></div>)}
          <div className="flex justify-between text-sm font-semibold border-t border-border mt-1 pt-1"><span>Total produits</span><Money value={f.total_produits} cur={cur} /></div>
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-1 mt-3">Charges</div>
          {f.charges.map((c) => <div key={c.code} className="flex justify-between text-sm py-0.5"><span>{c.code} {c.name}</span><Money value={c.amount} cur={cur} /></div>)}
          <div className="flex justify-between text-sm font-semibold border-t border-border mt-1 pt-1"><span>Total charges</span><Money value={f.total_charges} cur={cur} /></div>
          <div className="flex justify-between font-display font-bold text-primary border-t border-border mt-2 pt-2"><span>RÉSULTAT NET</span><Money value={f.resultat} cur={cur} /></div>
        </Card>

        <Card className="rounded-md border-border shadow-sm overflow-hidden" data-testid="balance-report">
          <div className="px-5 py-3 font-display font-semibold border-b border-border">Balance générale</div>
          <Table><TableHeader><TableRow><TableHead>Compte</TableHead><TableHead className="text-right">Débit</TableHead><TableHead className="text-right">Crédit</TableHead></TableRow></TableHeader>
            <TableBody>{f.balance.map((r) => <TableRow key={r.code}><TableCell className="text-sm"><span className="font-mono text-xs mr-2">{r.code}</span>{r.name}</TableCell><TableCell className="text-right"><Money value={r.debit} cur={cur} /></TableCell><TableCell className="text-right"><Money value={r.credit} cur={cur} /></TableCell></TableRow>)}</TableBody></Table>
        </Card>
      </div>
    </div>
  );
}
